import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, watch, onMounted, getCurrentInstance } from 'vue'
//@ts-ignore
import draggable from 'vuedraggable'
import deepEqual from 'deep-equal'
import sortBy from 'lodash/sortBy'
import types from '@/store/all-mutation-types'
import { buildDataMissionKey, DataPoolMissionKeys } from '@/enum/DataPool'
import { GliderFileType } from '@/enum/GliderFileType'
import { IFileTransfertConfig } from '@/models/iris/fileTransfert/IFileTransfertConfig'
import { MissionUi } from '@/models/ui/MissionUi'
import { IrisConfigContainer } from '@/models/iris/IrisConfigContainer'
import { AloGliderFileShort } from '../../models/alo/AloGliderFileShort'
import apiHelpers from '@/utils/api'
import { ensureStringArray, formatListFileEntry, parseListFileEntry } from '@/utils/fileTransfertList'
import missionApi from '../../api/mission'
import { SecurityItemsKeys } from '../../enum/SecurityItems'
import { VuetifyConfirmObject } from 'vuetify-confirm'
import store from '@/store'
import { AloMission } from '@/models/alo/AloMission'
import { i18n } from '@/locale/vueI18n'


export default /*#__PURE__*/_defineComponent({
  __name: 'MissionDetailFileTransfer',
  props: {
    mission: null
  },
  setup(__props: any) {

const props = __props as {
  mission: AloMission
};



// Accès aux plugins Vue via l'instance (Vue 2 + Composition API)
const instance = getCurrentInstance()
const $confirm = instance?.proxy?.$confirm
const boxes = ref([])
const config = ref({
  enableDownload: true,
  enableCatchUpNav: true,
  enableCatchUpPld: true,
  nbDownLastNavFile: 1,
  nbDownLastPldFile: 1,
  priority: ['standard', 'listFixed', 'multi_yo'],
  firstFile: 'false',
  lastFile: 'false',
  specificsNavFiles: [] as Array<number>,
  specificsPldFiles: [] as Array<number>,
  // Entrées de `list_file` que le front ne sait pas décoder : conservées telles
  // quelles et ré-émises au save, sinon un format IRIS inconnu serait détruit.
  otherFiles: [] as Array<string>,
  specificsFilesNf: [] as Array<string>,
  filesRequested: [] as Array<string>,
})

const newFileNfInput = ref('')

const multiYoFileItems = computed(() => [
  { text: i18n.t('Pilot.FileTransfert.OptionNone').toString(), value: 'false' },
  { text: i18n.t('Pilot.FileTransfert.NavFieldName').toString(), value: 'nav' },
  { text: i18n.t('Pilot.FileTransfert.PldFieldName').toString(), value: 'sci' },
  { text: i18n.t('Pilot.FileTransfert.OptionAll').toString(), value: 'all' },
])

const loading = computed(() => store.myGetters.fileTransfertLoading)
const status = computed(() => store.myGetters.gliderStatus)
const missionSecurity = computed(() => props.mission.security)
const accessConfigFileTransfert = computed(
  () => props.mission.security[SecurityItemsKeys.PILOT_DETAIL_FILE_TRANSFERT_CONFIG],
)
const loadingData = computed(() => !props.mission._downloadFileStatusLoaded)
const filesStatus = computed(() => {
  if (!props.mission.downloadFileStatus || !Array.isArray(props.mission.downloadFileStatus)) {
    return []
  }
  // @ts-ignore
  return Object.values(
    props.mission.downloadFileStatus.reduce((acc: any, value: any) => {
      // on ajoute la ligne si le fichier est downloadable ou la mission est active
      // if(props.mission.misActifOn || value.isDownloadable) {
      // filtre les cycles 0
      if (value.targetYosCycle != 0) {
        acc[value.targetYosCycle] = acc[value.targetYosCycle] || {
          yosCycle: value.targetYosCycle,
          nav: null,
          pld1: null,
          pld2: null,
        }
        if (value.gliderFileType == GliderFileType.gli) {
          acc[value.targetYosCycle].nav = value.dataProgression
          acc[value.targetYosCycle].navDownloadable = value.isDownloadable
          acc[value.targetYosCycle].navRunning = value.isRunning
        } else if (value.gliderFileType == GliderFileType.pld1) {
          acc[value.targetYosCycle].pld1 = value.dataProgression
          acc[value.targetYosCycle].pld1Downloadable = value.isDownloadable
          acc[value.targetYosCycle].pld1Running = value.isRunning
        } else if (value.gliderFileType == GliderFileType.pld2) {
          acc[value.targetYosCycle].pld2 = value.dataProgression
          acc[value.targetYosCycle].pld2Downloadable = value.isDownloadable
          acc[value.targetYosCycle].pld2Running = value.isRunning
        }
      }
      // }
      return acc
    }, {}),
  )
})

const downloadStatus = computed(() => null)

const originalFileTransfertConfig = computed(() => {
  if (props.mission.gliId == null) return null
  return store.myGetters.originalFileTransfertConfigs[props.mission.gliId]
})

const modifiedFileTransfertConfig = computed(() => {
  if (props.mission.gliId == null) return null
  return store.myGetters.modifiedFileTransfertConfigs[props.mission.gliId]
})

const btnActif = computed(() => {
  const configCopy = {}
  if (originalFileTransfertConfig.value) {
    loadConfig(configCopy, originalFileTransfertConfig.value)
  }
  return config.value.enableDownload && !deepEqual(configCopy, config.value) && !loading.value
})

const orderedSpecificsNavFiles = computed(() => sortBy(config.value.specificsNavFiles, Number))
const orderedSpecificsPldFiles = computed(() => sortBy(config.value.specificsPldFiles, Number))

// Multi-yo first/last file : IRIS utilise 'nav'|'sci'|'all' (string) et `false`
// (boolean) pour « aucun ». Le select travaille en chaîne ; on renvoie le booléen
// false à IRIS pour « aucun » (fidèle au format retourné).
function normalizeMultiYoFile(v: string | boolean | null | undefined): string {
  return typeof v === 'string' ? v : 'false'
}
function serializeMultiYoFile(v: string): string | boolean {
  return v === 'false' ? false : v
}

function loadConfig(target: any, configData: IrisConfigContainer<IFileTransfertConfig>) {
  if (configData) {
    target.enableDownload = configData.mod_settings.EnabledMOD['module-FileTransfer']
    target.enableCatchUpNav = configData.mod_settings.FileTransfer_standard.caught_old_file_nav
    target.enableCatchUpPld = configData.mod_settings.FileTransfer_standard.caught_old_file_sci
    target.nbDownLastNavFile = configData.mod_settings.FileTransfer_standard.nb_file_nav
    target.nbDownLastPldFile = configData.mod_settings.FileTransfer_standard.nb_file_sci
    target.priority = configData.mod_settings.FileTransfer
      ? [...configData.mod_settings.FileTransfer.priority_transferType]
      : []
    // multi_yo : si absent de la priorité (config legacy), on l'ajoute en dernière position
    if (target.priority.indexOf('multi_yo') == -1) target.priority.push('multi_yo')
    // IRIS renvoie `false` (boolean) pour « aucun » → normalisé en chaîne 'false'
    // (les autres valeurs 'nav'/'sci'/'all' sont des chaînes) pour le v-model du select.
    target.firstFile = normalizeMultiYoFile(configData.mod_settings.FileTransfer_multiYo?.first_file)
    target.lastFile = normalizeMultiYoFile(configData.mod_settings.FileTransfer_multiYo?.last_file)
    const pld = [] as Array<number>
    const nav = [] as Array<number>
    const other = [] as Array<string>
    // Le multi-files IRIS a changé le format : `nav|7` est ré-écrit `n7`. On lit
    // les deux (cf utils/fileTransfertList) et on préserve l'inconnu.
    ensureStringArray(configData.mod_settings.FileTransfer_listFile.list_file).forEach(it => {
      const entry = parseListFileEntry(it)
      if (entry == null) other.push(it)
      else if (entry.kind == 'nav') nav.push(entry.cycle)
      else pld.push(entry.cycle)
    })
    target.specificsNavFiles = nav
    target.specificsPldFiles = pld
    target.otherFiles = other
    target.specificsFilesNf = ensureStringArray(configData.mod_settings.FileTransfer_listFile.list_file_nf)
    target.filesRequested = ensureStringArray(configData.mod_settings.FileTransfer_state.gli_file_waiting)
  }
}

function orderup(item: string) {
  const index = config.value.priority.indexOf(item)
  const newIndex = (config.value.priority.length + index - 1) % config.value.priority.length
  config.value.priority.splice(newIndex, 0, config.value.priority.splice(index, 1)[0])
}

function orderdown(item: string) {
  const index = config.value.priority.indexOf(item)
  const newIndex = (config.value.priority.length + index + 1) % config.value.priority.length
  config.value.priority.splice(newIndex, 0, config.value.priority.splice(index, 1)[0])
}

function layoutBoxes() {
  // Animation logic removed for simplicity in script setup
}

function addToNavRequested(cycle: number) {
  if (config.value.specificsNavFiles.length > 10) {
    config.value.specificsNavFiles = config.value.specificsNavFiles.slice(0, 10)
    if ($confirm && $t) {
      $confirm(i18n.t('Pilot.FileTransfert.TooMuchFile').toString(), {
        title: i18n.t('Admin.Warning'),
        buttonTrueText: 'Ok',
        buttonFalseText: '',
      } as VuetifyConfirmObject)
    }
  } else {
    config.value.specificsNavFiles.push(cycle)
  }
}

function addToPldRequested(cycle: number) {
  if (config.value.specificsPldFiles.length > 10) {
    config.value.specificsPldFiles = config.value.specificsPldFiles.slice(0, 10)
    if ($confirm && $t) {
      $confirm(i18n.t('Pilot.FileTransfert.TooMuchFile').toString(), {
        title: i18n.t('Admin.Warning'),
        buttonTrueText: 'Ok',
        buttonFalseText: '',
      } as VuetifyConfirmObject)
    }
  } else {
    config.value.specificsPldFiles.push(cycle)
  }
}

function removeFromNavRequested(cycle: number) {
  config.value.specificsNavFiles.splice(config.value.specificsNavFiles.indexOf(cycle), 1)
}

function removeFromPldRequested(cycle: number) {
  config.value.specificsPldFiles.splice(config.value.specificsPldFiles.indexOf(cycle), 1)
}

function addFileNf() {
  const value = newFileNfInput.value.trim()
  // ajout tel quel, on évite juste les doublons exacts (règle de format à venir côté client)
  if (value && config.value.specificsFilesNf.indexOf(value) == -1) {
    config.value.specificsFilesNf.push(value)
  }
  newFileNfInput.value = ''
}

function removeFileNf(value: string) {
  config.value.specificsFilesNf.splice(config.value.specificsFilesNf.indexOf(value), 1)
}

function validateConfig() {
  store.myActions.GLIDER_UPDATE_MODIFIED_FILE_TRANSFERT({
    itemId: props.mission.gliId!,
    newProperties: {
      mod_settings: {
        EnabledMOD: {
          'module-FileTransfer': config.value.enableDownload,
        },
        FileTransfer: {
          priority_transferType: [...config.value.priority],
        },
        FileTransfer_multiYo: {
          // 'false' → booléen false (fidèle à IRIS), sinon la chaîne 'nav'/'sci'/'all'
          first_file: serializeMultiYoFile(config.value.firstFile),
          last_file: serializeMultiYoFile(config.value.lastFile),
        },
        FileTransfer_listFile: {
          list_file: [
            ...config.value.specificsNavFiles.map(it => formatListFileEntry('nav', it)),
            ...config.value.specificsPldFiles.map(it => formatListFileEntry('pld', it)),
            ...config.value.otherFiles,
          ],
          list_file_nf: [...config.value.specificsFilesNf],
        },
        FileTransfer_standard: {
          caught_old_file_nav: config.value.enableCatchUpNav,
          caught_old_file_sci: config.value.enableCatchUpPld,
          nb_file_nav: config.value.nbDownLastNavFile,
          nb_file_sci: config.value.nbDownLastPldFile,
        },
      },
    },
  })
}

function saveConfig() {
  validateConfig()
  store.myActions.GLIDER_IRIS_FILE_TRANSFERT_SAVE_REQUEST(props.mission.gliId!)
}

function restoreConfig() {
  store.myActions.GLIDER_CANCEL_MODIFIED_FILE_TRANSFERT(props.mission.gliId!)
}

function downloadFile(url: string) {
  apiHelpers.downloadFile(url, '.csv')
}

// Watch for mission changes
watch(
  () => props.mission,
  newValue => {
    if (newValue.misId != null) {
      store.myActions.MISSION_ENSURE_DOWNLOAD_FILE_STATUS(newValue.misId)
    }
    if (props.mission.misActifOn && accessConfigFileTransfert.value.enable && newValue.gliId != null) {
      store.myActions.GLIDER_IRIS_FILE_TRANSFERT_REQUEST(newValue.gliId)
    }
  },
)

// Watch for priority changes
watch(
  () => config.value.priority,
  (newValue: Array<string>) => {
    layoutBoxes()
  },
)

// Watch for modifiedFileTransfertConfig changes
watch(modifiedFileTransfertConfig, (newConfig: IrisConfigContainer<IFileTransfertConfig> | null) => {
  if (newConfig) {
    loadConfig(config.value, newConfig)
  }
})

// Watch for config changes
watch(
  config,
  () => {
    validateConfig()
  },
  { deep: true },
)

// Created lifecycle
if (props.mission.misId != null) {
  store.myActions.MISSION_ENSURE_DOWNLOAD_FILE_STATUS(props.mission.misId)
}
if (props.mission.misActifOn && accessConfigFileTransfert.value.enable && props.mission.gliId != null) {
  store.myActions.GLIDER_IRIS_FILE_TRANSFERT_REQUEST(props.mission.gliId).then(() => {
    if (modifiedFileTransfertConfig.value) {
      loadConfig(config.value, modifiedFileTransfertConfig.value)
    }
  })
}

return { __sfc: true,props, instance, $confirm, boxes, config, newFileNfInput, multiYoFileItems, loading, status, missionSecurity, accessConfigFileTransfert, loadingData, filesStatus, downloadStatus, originalFileTransfertConfig, modifiedFileTransfertConfig, btnActif, orderedSpecificsNavFiles, orderedSpecificsPldFiles, normalizeMultiYoFile, serializeMultiYoFile, loadConfig, orderup, orderdown, layoutBoxes, addToNavRequested, addToPldRequested, removeFromNavRequested, removeFromPldRequested, addFileNf, removeFileNf, validateConfig, saveConfig, restoreConfig, downloadFile }
}

})